import { createAsyncThunk } from "@reduxjs/toolkit"

import type {
    IOneVerifyEraUploadPresignRequestData,
    IOneVerifyIntegrationSupportRequestData,
} from "@common-shared/types/requestData/oneVerifyRequestData.types"
import type { IOneVerifyEraUploadCompleteResponse } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { ToastrHelper } from "@front-shared/helpers/toastr.helper"

import { EraUploadFileUploadStatus } from "../../config/integrations.config"
import { IntegrationsService } from "../../services/integrations.service"
import type { IThunkActionWithPracticeData } from "../../types/general.types"
import type { IIntegrationsEraUploadBatchThunkPayload } from "../../types/integrations.types"
import {
    runtimeStartLoading,
    runtimeStopLoading,
} from "../slices/runtime.slice"

export const INTEGRATIONS_SUPPORT_REQUEST_LOADING_KEY =
    "integrationsSupportRequestLoading"

export const INTEGRATIONS_ERA_UPLOAD_LOADING_KEY =
    "integrationsEraUploadLoading"

export const integrationsSubmitSupportRequest = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IOneVerifyIntegrationSupportRequestData>
>("integrationsSubmitSupportRequestPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading(INTEGRATIONS_SUPPORT_REQUEST_LOADING_KEY))

    try {
        const result =
            await IntegrationsService.submitIntegrationSupportRequest(
                action.practice,
                action.payload,
            )

        if (result) {
            ToastrHelper.success(
                "Thanks — we received your request and will follow up.",
            )

            action.onSuccess?.(result)
        } else {
            ToastrHelper.error(
                "Sorry, we couldn't submit your request. Please try again later.",
            )

            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading(INTEGRATIONS_SUPPORT_REQUEST_LOADING_KEY))

        action.onFinally?.()
    }
})

export const integrationsUploadEraFiles = createAsyncThunk<
    IOneVerifyEraUploadCompleteResponse[] | undefined,
    IThunkActionWithPracticeData<IIntegrationsEraUploadBatchThunkPayload>
>("integrationsUploadEraFilesPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading(INTEGRATIONS_ERA_UPLOAD_LOADING_KEY))

    const completedUploads: IOneVerifyEraUploadCompleteResponse[] = []
    let succeededCount = 0
    let failedCount = 0

    try {
        const totalAttempted = action.payload.files.length

        action.payload.onUploadProgress?.({
            uploadedCount: 0,
            totalCount: totalAttempted,
        })

        for (const filePayload of action.payload.files) {
            action.payload.onFileStatusChange?.(filePayload.fileId, {
                uploadStatus: EraUploadFileUploadStatus.uploading,
                uploadError: undefined,
            })

            const presignPayload: IOneVerifyEraUploadPresignRequestData = {
                fileName: filePayload.file.name,
                fileSizeBytes: filePayload.file.size,
                fileFormat: filePayload.fileFormat,
                batchId: action.payload.batchId,
                batchTimestamp: action.payload.batchTimestamp,
                fileId: filePayload.fileId,
            }

            const presign =
                await IntegrationsService.createEraUploadPresignedUrl(
                    action.practice,
                    presignPayload,
                )

            if (!presign) {
                failedCount += 1

                action.payload.onFileStatusChange?.(filePayload.fileId, {
                    uploadStatus: EraUploadFileUploadStatus.failed,
                    uploadError:
                        "Could not start the upload. Please try again.",
                })

                continue
            }

            const uploaded = await IntegrationsService.uploadFileToPresignedUrl(
                presign.uploadUrl,
                filePayload.file,
                presign.contentType,
            )

            if (!uploaded) {
                failedCount += 1

                action.payload.onFileStatusChange?.(filePayload.fileId, {
                    uploadStatus: EraUploadFileUploadStatus.failed,
                    uploadError:
                        "The file upload to storage failed. Please try again.",
                })

                continue
            }

            const complete = await IntegrationsService.completeEraUpload(
                action.practice,
                presign.uploadId,
                {
                    fileSizeBytes: filePayload.file.size,
                    fileFormat: filePayload.fileFormat,
                },
            )

            if (!complete) {
                failedCount += 1

                action.payload.onFileStatusChange?.(filePayload.fileId, {
                    uploadStatus: EraUploadFileUploadStatus.failed,
                    uploadError:
                        "The file was uploaded but we could not confirm it. Try again or contact support.",
                })

                continue
            }

            succeededCount += 1

            completedUploads.push(complete)

            action.payload.onFileStatusChange?.(filePayload.fileId, {
                uploadStatus: EraUploadFileUploadStatus.succeeded,
                uploadError: undefined,
            })

            action.payload.onUploadProgress?.({
                uploadedCount: succeededCount,
                totalCount: totalAttempted,
            })
        }

        if (succeededCount === totalAttempted) {
            action.onSuccess?.(completedUploads)

            return completedUploads
        }

        if (succeededCount > 0) {
            ToastrHelper.error(
                `${succeededCount} of ${totalAttempted} files uploaded. Retry the failed file${failedCount === 1 ? "" : "s"}.`,
            )
        } else {
            ToastrHelper.error(
                "Sorry, we couldn't upload your files. Please try again.",
            )
        }

        action.onError?.(undefined)

        return completedUploads.length ? completedUploads : undefined
    } catch (e) {
        action.onError?.(undefined)

        return completedUploads.length ? completedUploads : undefined
    } finally {
        dispatch(runtimeStopLoading(INTEGRATIONS_ERA_UPLOAD_LOADING_KEY))

        action.onFinally?.()
    }
})
